import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Stack, useClickOutside } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { createPortal } from "react-dom";

import style from "./MenuPortal.module.css";

export const OverlayContext = React.createContext<{
  overlayRef: RefObject<HTMLDivElement> | undefined;
}>({ overlayRef: undefined });

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const overlayRef = useRef(null);
  return (
    <>
      <OverlayContext.Provider value={{ overlayRef }}>{children}</OverlayContext.Provider>
      <div ref={overlayRef}></div>
    </>
  );
};

type MenuPortalChildren = {
  Item: typeof MenuItem;
  Divider: typeof MenuDivider;
};

interface MenuPortalProps {
  children: ReactNode;
  /**
   * The element that triggers the menu portal.
   * Must be a React element that accepts both `onClick` and `ref` props.
   * The element must be able to forward refs as the positioning system depends on it.
   * @example
   * <Button ref={ref} onClick={onClick}>Trigger</Button>
   */
  activator: ReactElement<{
    onClick: React.MouseEventHandler;
    ref: React.RefAttributes<HTMLElement>["ref"];
    children: ReactNode;
  }>;
  onOpen?: () => void;
  onClose?: () => void;
  onClick?: React.MouseEventHandler;
  matchActivatorWidth?: boolean;
  dropdownPosition?: "left" | "right";
  className?: string;
}

export const useDropdownPlacement = (
  open: boolean,
  activatorRef: React.RefObject<HTMLElement>,
  menuRef: React.RefObject<HTMLDivElement>,
  dropdownPosition?: "left" | "right"
): React.CSSProperties => {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const updatePosition = useCallback(() => {
    if (!open || !activatorRef.current || !menuRef.current) return;

    const activatorRect = activatorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewPortWidth = window.innerWidth;
    const viewPortHeight = window.innerHeight;
    const dropdownVerticalOffset = 4;
    const dropdownHorizontalOffset = 0;

    let top: number;
    const spaceBelow = viewPortHeight - activatorRect.bottom;

    if (menuRect.height <= spaceBelow) {
      top = activatorRect.bottom + dropdownVerticalOffset;
    } else {
      top = activatorRect.top - menuRect.height - dropdownVerticalOffset;
    }

    let left: number;
    if (dropdownPosition === "left") {
      left = activatorRect.right - menuRect.width - dropdownHorizontalOffset;
      if (left < 0) {
        left = activatorRect.left + dropdownHorizontalOffset;
      }
    } else {
      left = activatorRect.left + dropdownHorizontalOffset;
      if (left + menuRect.width > viewPortWidth) {
        left = activatorRect.right - menuRect.width - dropdownHorizontalOffset;
      }
    }

    setStyle({ top, left });
  }, [open, dropdownPosition, activatorRef, menuRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  const updatePositionCallback = useCallback(() => {
    requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener("scroll", updatePositionCallback);
    window.addEventListener("resize", updatePositionCallback);

    return () => {
      window.removeEventListener("scroll", updatePositionCallback);
      window.removeEventListener("resize", updatePositionCallback);
    };
  }, [open, updatePositionCallback]);

  return style;
};

export const MenuPortal = forwardRef<{ close: () => void }, MenuPortalProps>(
  (
    {
      children,
      className,
      activator,
      matchActivatorWidth,
      onOpen,
      onClose,
      onClick,
      dropdownPosition = "right",
    },
    ref
  ) => {
    const { overlayRef } = useContext(OverlayContext);
    const [open, setOpen] = useState(false);
    const clickOutsideTarget = useRef(null);
    const id = useId();
    const activatorRef = useRef<HTMLDivElement>(null);

    const closeCallback = useCallback(() => {
      setOpen(false);
      if (open) onClose?.();
    }, [open, setOpen, onClose]);

    const openCallback = useCallback(() => {
      setOpen(true);
      if (!open) onOpen?.();

      const closeMenu = () => {
        closeCallback();
        document.removeEventListener("scroll", closeMenu);
      };
      document.addEventListener("scroll", closeMenu);

      return () => document.removeEventListener("scroll", closeMenu);
    }, [open, setOpen, onOpen, closeCallback]);

    const menuRef = useRef<HTMLDivElement>(null);

    const dropdownStyle = useDropdownPlacement(open, activatorRef, menuRef, dropdownPosition);

    useClickOutside(clickOutsideTarget.current, closeCallback);
    useImperativeHandle(
      ref,
      () => ({
        close: closeCallback,
      }),
      [closeCallback]
    );

    return (
      <div ref={clickOutsideTarget} className={classNames(style.menuWrapper, className)}>
        {cloneElement(activator, {
          ref: activatorRef,
          onClick: (event: React.MouseEvent) => {
            if (open) {
              closeCallback();
            } else {
              openCallback();
            }

            activator.props.onClick?.(event);
          },
        })}
        {open &&
          createPortal(
            <Stack
              role="menu"
              style={dropdownStyle}
              className={classNames(style.menu, matchActivatorWidth && style.fullWidth)}
              direction={"column"}
              onClick={onClick}
              ref={menuRef}
            >
              {Children.map(children, (child) => {
                if (child && isValidElement(child) && child.props.closeOnClick) {
                  const c = child as MenuPortalProps["activator"];
                  return cloneElement(
                    c,
                    {
                      onClick: (event: React.MouseEvent) => {
                        if (open) {
                          closeCallback();
                        } else {
                          openCallback();
                        }
                        c.props.onClick?.(event);
                      },
                    },
                    c.props.children
                  );
                }

                return child;
              })}
            </Stack>,
            //@ts-expect-error It should be defined, otherwise we should crash in here locally
            overlayRef.current,
            id
          )}
      </div>
    );
  }
) as React.ForwardRefExoticComponent<
  MenuPortalProps & React.RefAttributes<{ close: () => void }>
> & {
  [key in keyof MenuPortalChildren]: MenuPortalChildren[key];
};
MenuPortal.displayName = "Menu";

const MenuItem: React.FC<{
  children: ReactNode;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  closeOnClick?: boolean;
}> = ({ children, disabled, onClick }) => {
  return (
    <button
      role="menuitem"
      className={classNames(style.menuItem)}
      disabled={disabled}
      onClick={(event: React.MouseEvent) => onClick?.(event)}
    >
      {children}
    </button>
  );
};
MenuPortal.Item = MenuItem;

const MenuDivider: React.FC = () => {
  return <hr role="separator" className={style.menuDivider}></hr>;
};
MenuPortal.Divider = MenuDivider;
